import { supabase } from '@/integrations/supabase/client';
import { fetchPaginated } from '@/lib/supabase-utils';

export interface IdleLogRow {
  id?: string;
  user_id: string;
  idle_start: string;
  idle_end: string | null;
  duration_seconds: number | null;
  time_log_id?: string | null;
  project_id?: string | null;
  organization_id?: string | null;
  time_logs?: { project_id: string | null } | null;
}

interface OrgContext {
  organizationId?: string | null;
  isSuperAdmin?: boolean;
}

interface FetchOptions {
  userId?: string;
  projectId?: string;
}

export async function fetchIdleLogs(
  start: Date,
  end: Date,
  ctx: OrgContext,
  opts?: FetchOptions
): Promise<IdleLogRow[]> {
  const timeLogRelation = opts?.projectId
    ? 'time_logs!idle_logs_time_log_id_fkey!inner(project_id)'
    : 'time_logs!idle_logs_time_log_id_fkey(project_id)';

  let query = (supabase
    .from('idle_logs')
    .select(`id, user_id, time_log_id, idle_start, idle_end, duration_seconds, organization_id, ${timeLogRelation}`)
    .gte('idle_start', start.toISOString())
    .lte('idle_start', end.toISOString())
    .order('idle_start', { ascending: false }) as any);

  if (opts?.userId) {
    query = query.eq('user_id', opts.userId);
  }

  if (opts?.projectId) {
    query = query.eq('time_logs.project_id', opts.projectId);
  }

  if (ctx.organizationId && !ctx.isSuperAdmin) {
    query = query.eq('organization_id', ctx.organizationId);
  }

  const rows = await fetchPaginated<IdleLogRow>(query);
  return rows.map((row) => ({
    ...row,
    project_id: row.time_logs?.project_id ?? null,
  }));
}

export function computeIdleStats(logs: IdleLogRow[]) {
  const totalIdleSeconds = logs.reduce((sum, log) => sum + (log.duration_seconds || 0), 0);
  const uniqueUsers = new Set(logs.map((l) => l.user_id));
  return {
    totalIdleSeconds,
    totalIdleHours: Math.round((totalIdleSeconds / 3600) * 100) / 100,
    uniqueUserCount: uniqueUsers.size,
    logCount: logs.length,
  };
}
